import React, { useState, useEffect } from "react";
import { Dropdown } from "react-bootstrap";
import { 
  FaDownload, 
  FaFileCsv, 
  FaFilePdf, 
  FaFileExcel, 
  FaTrash, 
  FaEnvelope,
  FaCalendarAlt,
  FaEdit,
  FaTimes,
  FaCheckCircle
} from "react-icons/fa";
import AppointmentService from '../services/appointmentTimelineService';

const AppointmentActionButton = ({ selectedAppointments = [], onBulkDelete, refreshData, apiBaseUrl = '/api' }) => {
  const [isDeleting, setIsDeleting] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [allSameStatus, setAllSameStatus] = useState(true);
  const [currentStatus, setCurrentStatus] = useState(null);
  const [showRescheduleModal, setShowRescheduleModal] = useState(false);
  const [showStatusModal, setShowStatusModal] = useState(false);
  const [newDate, setNewDate] = useState('');
  const [newTime, setNewTime] = useState('');
  const [newStatus, setNewStatus] = useState('');

  // Initialize service instance
  const appointmentService = new AppointmentService();

  // Check if all selected appointments have the same status
  useEffect(() => {
    if (selectedAppointments.length > 0) {
      const firstStatus = selectedAppointments[0].status || 'scheduled';
      
      const allSame = selectedAppointments.every(appointment => {
        const status = appointment.status || 'scheduled';
        return status === firstStatus;
      });
      
      setAllSameStatus(allSame);
      setCurrentStatus(firstStatus);
    } else {
      setAllSameStatus(true);
      setCurrentStatus(null);
    }
  }, [selectedAppointments]);

  const exportToPDF = (appointments) => {
    if (appointments.length === 0) return;

    try {
      const printWindow = window.open('', '', 'width=800,height=600');
      
      const appointmentsHTML = appointments.map(appointment => {
        const formatDate = (dateString) => {
          if (!dateString) return "N/A";
          try {
            return new Date(dateString).toLocaleDateString();
          } catch {
            return dateString;
          }
        };
        
        return `
          <div class="appointment-block">
            <h3>Appointment: ${appointment.appointmentId || appointment.id || appointment._id}</h3>
            <div class="appointment-details">
              <p><strong>Patient:</strong> ${appointment.patientName || 'N/A'}</p>
              <p><strong>Doctor:</strong> ${appointment.doctorName || 'N/A'}</p>
              <p><strong>Date:</strong> ${formatDate(appointment.date)}</p>
              <p><strong>Time:</strong> ${appointment.startTime || 'N/A'} - ${appointment.endTime || 'N/A'}</p>
              <p><strong>Status:</strong> <span class="status-${appointment.status}">${appointment.status || 'N/A'}</span></p>
              <p><strong>Type:</strong> ${appointment.appointmentType || 'N/A'}</p>
              <p><strong>Address:</strong> ${appointment.address || 'N/A'}</p>
              ${appointment.medicalHistory ? `<p><strong>Medical History:</strong> ${appointment.medicalHistory}</p>` : ''}
              ${appointment.notes ? `<p><strong>Notes:</strong> ${appointment.notes}</p>` : ''}
            </div>
          </div>
        `;
      }).join('');
      
      printWindow.document.write(`
        <html>
          <head>
            <title>Appointments Export</title>
            <style>
              body { 
                font-family: Arial, sans-serif; 
                margin: 20px;
              }
              .header { 
                text-align: center; 
                margin-bottom: 30px;
                border-bottom: 2px solid #333;
                padding-bottom: 10px;
              }
              .appointment-block {
                margin-bottom: 30px;
                padding: 15px;
                border: 1px solid #ddd;
                border-radius: 5px;
                page-break-inside: avoid;
              }
              .appointment-details p {
                margin: 5px 0;
              }
              .status-confirmed { color: #28a745; }
              .status-pending { color: #ffc107; }
              .status-cancelled { color: #dc3545; }
              @media print {
                .appointment-block {
                  page-break-inside: avoid;
                }
              }
            </style>
          </head>
          <body>
            <div class="header">
              <h1>Appointments Export</h1>
              <p>Generated on ${new Date().toLocaleDateString()}</p>
              <p>Total Appointments: ${appointments.length}</p>
            </div>
            ${appointmentsHTML}
            <script>
              window.onload = function() {
                window.print();
                window.onafterprint = function() {
                  window.close();
                }
              }
            </script>
          </body>
        </html>
      `);
      
      printWindow.document.close();
    } catch (error) {
      console.error('Error exporting to PDF:', error);
      alert('Failed to export to PDF: ' + error.message);
    }
  };

  const exportToCSV = (appointments) => {
    if (appointments.length === 0) return;

    try {
      const headers = [
        'ID',
        'Patient Name',
        'Date',
        'Start Time',
        'End Time',
        'Status',
        'Doctor Name',
        'Type',
        'Address',
        'Medical History',
        'Notes'
      ];
      
      const csvContent = [
        headers.join(','),
        ...appointments.map(appointment => [
          appointment.appointmentId || appointment.id || appointment._id,
          `"${appointment.patientName || 'N/A'}"`,
          appointment.date || 'N/A',
          appointment.startTime || 'N/A',
          appointment.endTime || 'N/A',
          appointment.status || 'N/A',
          `"${appointment.doctorName || 'N/A'}"`,
          `"${appointment.appointmentType || 'N/A'}"`,
          `"${appointment.address || 'N/A'}"`,
          `"${appointment.medicalHistory || 'N/A'}"`,
          `"${appointment.notes || 'N/A'}"`
        ].join(','))
      ].join('\n');
      
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      const url = URL.createObjectURL(blob);
      link.setAttribute('href', url);
      link.setAttribute('download', `appointments_${new Date().toISOString().split('T')[0]}.csv`);
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
      console.log(`Successfully exported ${appointments.length} appointments to CSV`);
    } catch (error) {
      console.error('Error exporting to CSV:', error);
      alert('Failed to export to CSV: ' + error.message);
    }
  };

  const exportToExcel = (appointments) => {
    // Fallback to CSV since SheetJS is not available
    console.log('Excel export would require SheetJS library. Falling back to CSV.');
    exportToCSV(appointments);
  };

  const printAppointments = (appointments) => {
    if (appointments.length === 0) return;

    try {
      const printWindow = window.open('', '', 'width=800,height=600');
      
      const appointmentsHTML = appointments.map(appointment => {
        const formatDate = (dateString) => {
          if (!dateString) return "N/A";
          try {
            return new Date(dateString).toLocaleDateString();
          } catch {
            return dateString;
          }
        };
        
        return `
          <div class="appointment-print">
            <h2>${appointment.patientName || 'N/A'} - ${formatDate(appointment.date)}</h2>
            <table class="appointment-table">
              <tr>
                <td><strong>Appointment ID:</strong></td>
                <td>${appointment.appointmentId || appointment.id || appointment._id}</td>
              </tr>
              <tr>
                <td><strong>Patient:</strong></td>
                <td>${appointment.patientName || 'N/A'}</td>
              </tr>
              <tr>
                <td><strong>Doctor:</strong></td>
                <td>${appointment.doctorName || 'N/A'}</td>
              </tr>
              <tr>
                <td><strong>Date & Time:</strong></td>
                <td>${formatDate(appointment.date)} ${appointment.startTime || 'N/A'} - ${appointment.endTime || 'N/A'}</td>
              </tr>
              <tr>
                <td><strong>Status:</strong></td>
                <td>${appointment.status || 'N/A'}</td>
              </tr>
              <tr>
                <td><strong>Type:</strong></td>
                <td>${appointment.appointmentType || 'N/A'}</td>
              </tr>
              ${appointment.medicalHistory ? `
              <tr>
                <td><strong>Medical History:</strong></td>
                <td>${appointment.medicalHistory}</td>
              </tr>` : ''}
              ${appointment.notes ? `
              <tr>
                <td><strong>Notes:</strong></td>
                <td>${appointment.notes}</td>
              </tr>` : ''}
            </table>
          </div>
        `;
      }).join('<div class="page-break"></div>');
      
      printWindow.document.write(`
        <html>
          <head>
            <title>Print Appointments</title>
            <style>
              body { 
                font-family: Arial, sans-serif; 
                margin: 0;
                padding: 20px;
              }
              .appointment-print {
                margin-bottom: 40px;
                padding: 20px;
                border: 2px solid #333;
              }
              .appointment-table {
                width: 100%;
                border-collapse: collapse;
                margin-top: 10px;
              }
              .appointment-table td {
                padding: 8px;
                border-bottom: 1px solid #ddd;
                vertical-align: top;
              }
              .appointment-table td:first-child {
                width: 200px;
                font-weight: bold;
              }
              .page-break {
                page-break-before: always;
              }
              @media print {
                .appointment-print {
                  page-break-after: always;
                }
                .appointment-print:last-child {
                  page-break-after: avoid;
                }
              }
            </style>
          </head>
          <body>
            <h1 style="text-align: center; margin-bottom: 30px;">Appointments (${appointments.length} total)</h1>
            ${appointmentsHTML}
            <script>
              window.onload = function() {
                window.print();
                window.onafterprint = function() {
                  window.close();
                }
              }
            </script>
          </body>
        </html>
      `);
      
      printWindow.document.close();
    } catch (error) {
      console.error('Error printing appointments:', error);
      alert('Failed to print appointments: ' + error.message);
    }
  };

  const sendReminders = async (appointments) => {
    if (appointments.length === 0) return;
    
    if (!window.confirm(`Are you sure you want to send reminders for ${appointments.length} appointments?`)) {
      return;
    }
    
    setIsProcessing(true);
    
    try {
      // Simulate API call for sending reminders
      await new Promise(resolve => setTimeout(resolve, 2000));
      
      alert(`Successfully sent reminders for ${appointments.length} appointments`);
      
      if (refreshData) {
        refreshData();
      }
    } catch (error) {
      console.error('Error sending reminders:', error);
      alert(`An error occurred while sending reminders: ${error.message}`);
    } finally {
      setIsProcessing(false);
    }
  };

  const cancelAppointments = async (appointments) => {
    if (appointments.length === 0) return;
    
    const appointmentsToCancel = appointments.filter(apt => apt.status !== 'cancelled');
    
    if (appointmentsToCancel.length === 0) {
      alert('All selected appointments are already cancelled.');
      return;
    }
    
    if (!window.confirm(`Are you sure you want to cancel ${appointmentsToCancel.length} appointments?`)) {
      return;
    }
    
    setIsProcessing(true);
    
    try {
      let successfulCancellations = 0;
      const cancellationErrors = [];
      
      for (const appointment of appointmentsToCancel) {
        try {
          const appointmentId = appointment.id || appointment._id;
          console.log('Cancelling appointment with ID:', appointmentId);
          
          await appointmentService.cancelAppointment(appointmentId, {
            reason: 'Bulk cancellation'
          });
          
          successfulCancellations++;
          console.log(`Successfully cancelled appointment ${appointmentId}`);
        } catch (error) {
          const appointmentId = appointment.id || appointment._id;
          console.error(`Error cancelling appointment ${appointmentId}:`, error);
          cancellationErrors.push(`ID ${appointmentId}: ${error.message || 'Unknown error'}`);
        }
      }
      
      if (successfulCancellations === appointmentsToCancel.length) {
        alert(`Successfully cancelled all ${appointmentsToCancel.length} appointments`);
      } else {
        const errorMessage = `Cancelled ${successfulCancellations} out of ${appointmentsToCancel.length} appointments. Errors: ${cancellationErrors.join('; ')}`;
        console.warn(errorMessage);
        alert(errorMessage);
      }
      
      console.log('Refreshing data after cancellation');
      if (refreshData) {
        refreshData();
      } else {
        window.location.reload();
      }
    } catch (error) {
      console.error('Error in bulk cancellation:', error);
      alert(`An unexpected error occurred: ${error.message}`);
      
      if (refreshData) {
        refreshData();
      }
    } finally {
      setIsProcessing(false);
    }
  };

  const deleteAppointments = async (appointments) => {
    if (appointments.length === 0) return;
    
    const idList = appointments.map(appointment => {
      return appointment._id || appointment.id;
    }).join(", ");
    
    if (!window.confirm(`Are you sure you want to delete the appointments with IDs: ${idList}?`)) {
      return;
    }
    
    setIsDeleting(true);
    
    try {
      let successfulDeletions = 0;
      const deletionErrors = [];
      
      for (const appointment of appointments) {
        try {
          const appointmentId = appointment._id || appointment.id;
          console.log('Deleting appointment with ID:', appointmentId);
          
          await appointmentService.deleteAppointment(appointmentId);
          successfulDeletions++;
          console.log(`Successfully deleted appointment ${appointmentId}`);
        } catch (error) {
          const appointmentId = appointment._id || appointment.id;
          console.error(`Error deleting appointment ${appointmentId}:`, error);
          deletionErrors.push(`ID ${appointmentId}: ${error.message || 'Unknown error'}`);
        }
      }
      
      if (successfulDeletions === appointments.length) {
        alert(`Successfully deleted all ${appointments.length} appointments`);
      } else {
        const errorMessage = `Deleted ${successfulDeletions} out of ${appointments.length} appointments. Errors: ${deletionErrors.join('; ')}`;
        console.warn(errorMessage);
        alert(errorMessage);
      }
      
      if (onBulkDelete && successfulDeletions > 0) {
        onBulkDelete(appointments.filter((p, index) => !deletionErrors.some(e => e.includes(p._id || p.id))));
      }
    } catch (error) {
      console.error('Error in bulk deletion:', error);
      alert(`An unexpected error occurred during deletion: ${error.message}`);
    } finally {
      setIsDeleting(false);
      
      console.log('Refreshing data after deletion');
      if (refreshData) {
        refreshData();
      } else {
        window.location.reload();
      }
    }
  };

  const handleStatusUpdate = async () => {
    if (!newStatus) {
      alert('Please select a status.');
      return;
    }

    if (selectedAppointments.length === 0) return;

    setIsProcessing(true);
    
    try {
      let successfulUpdates = 0;
      const updateErrors = [];
      
      for (const appointment of selectedAppointments) {
        try {
          const appointmentId = appointment.id || appointment._id;
          console.log('Updating status for appointment:', appointmentId, 'to:', newStatus);
          
          await appointmentService.updateAppointmentStatus(appointmentId, newStatus);
          
          successfulUpdates++;
          console.log(`Successfully updated status for appointment ${appointmentId}`);
        } catch (error) {
          const appointmentId = appointment.id || appointment._id;
          console.error(`Error updating status for appointment ${appointmentId}:`, error);
          updateErrors.push(`ID ${appointmentId}: ${error.message || 'Unknown error'}`);
        }
      }
      
      if (successfulUpdates === selectedAppointments.length) {
        alert(`Successfully updated status for ${successfulUpdates} appointments to "${newStatus}"`);
      } else {
        const errorMessage = `Updated status for ${successfulUpdates} out of ${selectedAppointments.length} appointments. Errors: ${updateErrors.join('; ')}`;
        console.warn(errorMessage);
        alert(errorMessage);
      }
      
      setShowStatusModal(false);
      setNewStatus('');
      
      if (refreshData) {
        refreshData();
      } else {
        window.location.reload();
      }
      
    } catch (error) {
      console.error('Error updating status:', error);
      alert(`An error occurred while updating status: ${error.message}`);
    } finally {
      setIsProcessing(false);
    }
  };

  const handleReschedule = async () => {
    if (!newDate || !newTime) {
      alert('Please select both date and time for rescheduling.');
      return;
    }

    if (selectedAppointments.length === 0) return;

    setIsProcessing(true);
    
    try {
      let successfulReschedules = 0;
      const rescheduleErrors = [];
      
      const addOneHourToTime = (timeString) => {
        try {
          const [hours, minutes] = timeString.split(':').map(Number);
          const newHours = (hours + 1) % 24;
          return `${newHours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}`;
        } catch (error) {
          console.error('Error adding hour to time:', error);
          return timeString;
        }
      };
      
      for (const appointment of selectedAppointments) {
        try {
          const appointmentId = appointment.id || appointment._id;
          console.log('Rescheduling appointment:', appointmentId);
          
          const rescheduleData = {
            date: newDate,
            startTime: newTime,
            endTime: addOneHourToTime(newTime),
            ...(appointment.userid && { userid: appointment.userid }),
            ...(appointment.meetinguserid && { meetinguserid: appointment.meetinguserid }),
            ...(appointment.patientName && { patientName: appointment.patientName }),
            ...(appointment.doctorName && { doctorName: appointment.doctorName }),
            ...(appointment.appointmentType && { appointmentType: appointment.appointmentType }),
            status: appointment.status || 'scheduled',
            ...(appointment.address && { address: appointment.address }),
            ...(appointment.notes && { notes: appointment.notes }),
            ...(appointment.medicalHistory && { medicalHistory: appointment.medicalHistory })
          };
          
          await appointmentService.rescheduleAppointment(appointmentId, rescheduleData);
          
          successfulReschedules++;
          console.log(`Successfully rescheduled appointment ${appointmentId}`);
        } catch (error) {
          const appointmentId = appointment.id || appointment._id;
          console.error(`Error rescheduling appointment ${appointmentId}:`, error);
          rescheduleErrors.push(`ID ${appointmentId}: ${error.message || 'Unknown error'}`);
        }
      }
      
      if (successfulReschedules === selectedAppointments.length) {
        alert(`Successfully rescheduled ${successfulReschedules} appointments to ${newDate} at ${newTime}`);
      } else {
        const errorMessage = `Rescheduled ${successfulReschedules} out of ${selectedAppointments.length} appointments. Errors: ${rescheduleErrors.join('; ')}`;
        console.warn(errorMessage);
        alert(errorMessage);
      }
      
      setShowRescheduleModal(false);
      setNewDate('');
      setNewTime('');
      
      if (refreshData) {
        refreshData();
      } else {
        window.location.reload();
      }
      
    } catch (error) {
      console.error('Error rescheduling appointments:', error);
      alert(`An error occurred while rescheduling: ${error.message}`);
    } finally {
      setIsProcessing(false);
    }
  };

  const getStatusColor = (status) => {
    switch (status?.toLowerCase()) {
      case 'confirmed':
        return 'success';
      case 'pending':
        return 'warning';
      case 'cancelled':
        return 'danger';
      case 'completed':
        return 'info';
      case 'in-progress':
        return 'primary';
      case 'no-show':
        return 'dark';
      default:
        return 'secondary';
    }
  };

  const handleActionClick = (action) => {
    if (selectedAppointments.length > 0) {
      console.log(`${action} clicked for ${selectedAppointments.length} appointments`);
      
      switch(action) {
        case "Print":
          printAppointments(selectedAppointments);
          break;
        case "Export CSV":
          exportToCSV(selectedAppointments);
          break;
        case "Export PDF":
          exportToPDF(selectedAppointments);
          break;
        case "Export Excel":
          exportToExcel(selectedAppointments);
          break;
        case "Send Reminders":
          sendReminders(selectedAppointments);
          break;
        case "Reschedule Multiple":
          setShowRescheduleModal(true);
          break;
        case "Update Status":
          setShowStatusModal(true);
          break;
        case "Cancel Appointments":
          cancelAppointments(selectedAppointments);
          break;
        case "Delete":
          deleteAppointments(selectedAppointments);
          break;
        default:
          console.warn(`Unknown action: ${action}`);
          break;
      }
    }
  };

  return (
    <>
      <div className="flex-grow-sm-0">
        <Dropdown>
          <Dropdown.Toggle
            id="action-dropdown"
            className={`btn btn-gray text-start w-100 d-flex align-items-center justify-content-between px-4 py-2 border-0 rounded-3 shadow-sm dropdownbtn`}
            disabled={isDeleting || isProcessing}
          >
            <span>
              {isDeleting ? 'Deleting...' : 
               isProcessing ? 'Processing...' : 
               `Action${selectedAppointments.length > 0 ? ` (${selectedAppointments.length})` : ''}`}
            </span>
          </Dropdown.Toggle>

          <Dropdown.Menu className="dropdown-menu w-100 bg-white fs-sm fw-semibold border-0 rounded-3 shadow-sm mt-2">
            <Dropdown.Item
              onClick={() => handleActionClick("Print")}
              className="dropdown-item d-flex align-items-center gap-2 py-2"
              disabled={selectedAppointments.length === 0 || isDeleting || isProcessing}
            >
              <FaDownload className="icon-sm" />
              <span>Print Appointments</span>
            </Dropdown.Item>

            <Dropdown.Divider />

            <Dropdown.Item
              onClick={() => handleActionClick("Export CSV")}
              className="dropdown-item d-flex align-items-center gap-2 py-2"
              disabled={selectedAppointments.length === 0 || isDeleting || isProcessing}
            >
              <FaFileCsv className="icon-sm" />
              <span>Download CSV</span>
            </Dropdown.Item>
            <Dropdown.Item
              onClick={() => handleActionClick("Export PDF")}
              className="dropdown-item d-flex align-items-center gap-2 py-2"
              disabled={selectedAppointments.length === 0 || isDeleting || isProcessing}
            >
              <FaFilePdf className="icon-sm" />
              <span>Download PDF</span>
            </Dropdown.Item>
            <Dropdown.Item
              onClick={() => handleActionClick("Export Excel")}
              className="dropdown-item d-flex align-items-center gap-2 py-2"
              disabled={selectedAppointments.length === 0 || isDeleting || isProcessing}
            >
              <FaFileExcel className="icon-sm" />
              <span>Download Excel</span>
            </Dropdown.Item>

            <Dropdown.Divider />

            <Dropdown.Item
              onClick={() => handleActionClick("Send Reminders")}
              className="dropdown-item d-flex align-items-center gap-2 py-2"
              disabled={selectedAppointments.length === 0 || isDeleting || isProcessing}
            >
              <FaEnvelope className="icon-sm" />
              <span>Send Reminders</span>
            </Dropdown.Item>

            <Dropdown.Divider />

            <Dropdown.Item
              onClick={() => handleActionClick("Reschedule Multiple")}
              className="dropdown-item d-flex align-items-center gap-2 py-2"
              disabled={selectedAppointments.length === 0 || isDeleting || isProcessing}
            >
              <FaCalendarAlt className="icon-sm" />
              <span>Reschedule Multiple</span>
            </Dropdown.Item>

            <Dropdown.Item
              onClick={() => handleActionClick("Update Status")}
              className="dropdown-item d-flex align-items-center gap-2 py-2"
              disabled={selectedAppointments.length === 0 || isDeleting || isProcessing}
            >
              <FaEdit className="icon-sm" />
              <span>Update Status</span>
            </Dropdown.Item>

            <Dropdown.Item
              onClick={() => handleActionClick("Cancel Appointments")}
              className="dropdown-item d-flex align-items-center gap-2 py-2"
              disabled={selectedAppointments.length === 0 || isDeleting || isProcessing}
            >
              <FaTimes className="icon-sm" />
              <span>Cancel Appointments</span>
            </Dropdown.Item>

            <Dropdown.Divider />

            <Dropdown.Item
              onClick={() => handleActionClick("Delete")}
              className="dropdown-item d-flex align-items-center gap-2 py-2"
              disabled={selectedAppointments.length === 0 || isDeleting || isProcessing}
            >
              <FaTrash className="icon-sm" />
              <span>{isDeleting ? 'Deleting...' : 'Delete Selected'}</span>
            </Dropdown.Item>
          </Dropdown.Menu>
        </Dropdown>
      </div>

      {/* Modal para Reprogramar Múltiples Citas */}
      {showRescheduleModal && (
        <div className="modal show d-block" tabIndex="-1" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
          <div className="modal-dialog">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">
                  <FaCalendarAlt className="me-2" />
                  Reschedule {selectedAppointments.length} Appointment{selectedAppointments.length > 1 ? 's' : ''}
                </h5>
                <button
                  type="button"
                  className="btn-close"
                  onClick={() => setShowRescheduleModal(false)}
                  disabled={isProcessing}
                ></button>
              </div>
              <div className="modal-body">
                <div className="alert alert-info">
                  <small>
                    All selected appointments will be rescheduled to the same date and time.
                    Make sure the new time slot is available for all appointments.
                  </small>
                </div>
                
                <div className="mb-3">
                  <label htmlFor="newDate" className="form-label">New Date</label>
                  <input
                    type="date"
                    className="form-control"
                    id="newDate"
                    value={newDate}
                    onChange={(e) => setNewDate(e.target.value)}
                    min={new Date().toISOString().split('T')[0]}
                    disabled={isProcessing}
                  />
                </div>
                
                <div className="mb-3">
                  <label htmlFor="newTime" className="form-label">New Time</label>
                  <input
                    type="time"
                    className="form-control"
                    id="newTime"
                    value={newTime}
                    onChange={(e) => setNewTime(e.target.value)}
                    disabled={isProcessing}
                  />
                </div>

                <div className="mb-3">
                  <h6>Selected Appointments:</h6>
                  <div className="list-group" style={{ maxHeight: '200px', overflowY: 'auto' }}>
                    {selectedAppointments.map((appointment, index) => (
                      <div key={index} className="list-group-item list-group-item-action">
                        <div className="d-flex w-100 justify-content-between">
                          <h6 className="mb-1">{appointment.patientName || 'N/A'}</h6>
                          <small>{appointment.date} {appointment.startTime}</small>
                        </div>
                        <p className="mb-1">{appointment.doctorName || 'N/A'}</p>
                        <small>Status: {appointment.status || 'scheduled'}</small>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => setShowRescheduleModal(false)}
                  disabled={isProcessing}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="btn btn-primary"
                  onClick={handleReschedule}
                  disabled={!newDate || !newTime || isProcessing}
                >
                  {isProcessing ? (
                    <>
                      <span className="spinner-border spinner-border-sm me-2" role="status"></span>
                      Rescheduling...
                    </>
                  ) : (
                    <>
                      <FaCheckCircle className="me-2" />
                      Reschedule All
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Modal para Actualizar Estado */}
      {showStatusModal && (
        <div className="modal show d-block" tabIndex="-1" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
          <div className="modal-dialog">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">
                  <FaEdit className="me-2" />
                  Update Status for {selectedAppointments.length} Appointment{selectedAppointments.length > 1 ? 's' : ''}
                </h5>
                <button
                  type="button"
                  className="btn-close"
                  onClick={() => setShowStatusModal(false)}
                  disabled={isProcessing}
                ></button>
              </div>
              <div className="modal-body">
                {allSameStatus && currentStatus && (
                  <div className="alert alert-info">
                    <small>
                      All selected appointments currently have status: <strong>{currentStatus}</strong>
                    </small>
                  </div>
                )}

                <div className="mb-3">
                  <label htmlFor="newStatus" className="form-label">New Status</label>
                  <select
                    className="form-select"
                    id="newStatus"
                    value={newStatus}
                    onChange={(e) => setNewStatus(e.target.value)}
                    disabled={isProcessing}
                  >
                    <option value="">Select new status...</option>
                    <option value="scheduled">Scheduled</option>
                    <option value="confirmed">Confirmed</option>
                    <option value="pending">Pending</option>
                    <option value="in-progress">In Progress</option>
                    <option value="completed">Completed</option>
                    <option value="cancelled">Cancelled</option>
                    <option value="no-show">No Show</option>
                  </select>
                </div>

                <div className="mb-3">
                  <h6>Selected Appointments:</h6>
                  <div className="list-group" style={{ maxHeight: '200px', overflowY: 'auto' }}>
                    {selectedAppointments.map((appointment, index) => (
                      <div key={index} className="list-group-item">
                        <div className="d-flex w-100 justify-content-between">
                          <h6 className="mb-1">{appointment.patientName || 'N/A'}</h6>
                          <small>{appointment.date} {appointment.startTime}</small>
                        </div>
                        <p className="mb-1">{appointment.doctorName || 'N/A'}</p>
                        <small>Current Status: <span className={`badge bg-${getStatusColor(appointment.status)}`}>
                          {appointment.status || 'scheduled'}
                        </span></small>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => setShowStatusModal(false)}
                  disabled={isProcessing}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="btn btn-primary"
                  onClick={handleStatusUpdate}
                  disabled={!newStatus || isProcessing}
                >
                  {isProcessing ? (
                    <>
                      <span className="spinner-border spinner-border-sm me-2" role="status"></span>
                      Updating...
                    </>
                  ) : (
                    <>
                      <FaCheckCircle className="me-2" />
                      Update Status
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default AppointmentActionButton;